import { useState } from 'react'
import {
  Stack, TextInput, Button, Alert, Group, Text, Badge, Loader,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import {
  IconLink, IconCheck, IconX, IconPlugConnected,
} from '@tabler/icons-react'
import { useLinkJira, useJiraOrgConfig } from '@/hooks/useOrganizations'
import { organizationsApi } from '@/api/organizations'
import { WidgetCard, CardHeader, EmptyState } from '@/components/ui'

interface Props {
  organizationId: number
  organizationName: string
  canEdit: boolean
}

export function JiraLinkPanel({ organizationId, organizationName, canEdit }: Props) {
  const { data: jiraConfig, isLoading } = useJiraOrgConfig(organizationId)
  const linkJira = useLinkJira()
  const [jiraUrl, setJiraUrl] = useState('')
  const [toggleLoading, setToggleLoading] = useState(false)

  const handleLink = async () => {
    if (!jiraUrl.trim()) return
    try {
      const result = await linkJira.mutateAsync({ orgId: organizationId, jiraUrl: jiraUrl.trim() })
      notifications.show({
        title: 'Jira vinculado',
        message: `Projeto ${result.parsed.project_key} conectado (${result.test_result.total_issues} issues)`,
        color: 'green',
      })
      setJiraUrl('')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Falha ao vincular Jira'
      notifications.show({ title: 'Erro', message, color: 'red' })
    }
  }

  const handleToggleSync = async () => {
    if (!jiraConfig) return
    setToggleLoading(true)
    try {
      await organizationsApi.toggleJiraSync(organizationId, !jiraConfig.sync_enabled)
      notifications.show({
        title: jiraConfig.sync_enabled ? 'Sync desativado' : 'Sync ativado',
        message: `Sincronização ${jiraConfig.sync_enabled ? 'pausada' : 'iniciada'}`,
        color: jiraConfig.sync_enabled ? 'orange' : 'green',
      })
    } catch {
      notifications.show({ title: 'Erro', message: 'Falha ao alterar sync', color: 'red' })
    }
    setToggleLoading(false)
  }

  if (isLoading) return <Loader size="sm" />

  if (jiraConfig) {
    return (
      <WidgetCard accent="info" p={0}>
        <CardHeader title={`Jira — ${organizationName}`} extra={<IconPlugConnected size={16} />} />
        <Stack p="md" gap="sm">
          <Group gap="xs">
            <Text size="sm" c="dimmed">Projeto:</Text>
            <Badge variant="light">{jiraConfig.jira_project_key}</Badge>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Board:</Text>
            <Badge variant="light">{jiraConfig.jira_board_id ?? '-'}</Badge>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">URL:</Text>
            <Text size="sm">{jiraConfig.jira_base_url}</Text>
          </Group>
          <Group gap="xs">
            <Text size="sm" c="dimmed">Sync:</Text>
            <Badge color={jiraConfig.sync_enabled ? 'green' : 'gray'}>
              {jiraConfig.sync_enabled ? 'Ativo' : 'Inativo'}
            </Badge>
            {canEdit && (
              <Button size="xs" variant="light" loading={toggleLoading} onClick={handleToggleSync}>
                {jiraConfig.sync_enabled ? 'Pausar' : 'Ativar'}
              </Button>
            )}
          </Group>

          {canEdit && (
            <>
              <Text size="xs" c="dimmed" mt="sm">Para alterar o projeto Jira, cole um novo link abaixo:</Text>
              <Group gap="sm">
                <TextInput
                  placeholder="https://org.atlassian.net/jira/software/projects/PROJ/boards/ID"
                  value={jiraUrl}
                  onChange={(e) => setJiraUrl(e.currentTarget.value)}
                  style={{ flex: 1 }}
                  size="sm"
                />
                <Button size="sm" onClick={handleLink} loading={linkJira.isPending} leftSection={<IconLink size={14} />}>
                  Atualizar
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </WidgetCard>
    )
  }

  if (!canEdit) {
    return (
      <EmptyState
        size="md"
        accent="info"
        icon={IconPlugConnected}
        title="Jira não configurado"
        description="Esta organização ainda não tem o Jira vinculado. Solicite a um gestor ou administrador para configurar a integração."
      />
    )
  }

  return (
    <WidgetCard accent="info" p={0}>
      <CardHeader title={`Vincular Jira — ${organizationName}`} extra={<IconPlugConnected size={16} />} />
      <Stack p="md" gap="md">
        <Text size="sm">
          Cole o link do board do Jira para conectar a organização. O sistema vai extrair automaticamente o projeto e o board.
        </Text>
        <TextInput
          label="Link do Jira"
          placeholder="https://org.atlassian.net/jira/software/projects/PROJ/boards/35/backlog"
          value={jiraUrl}
          onChange={(e) => setJiraUrl(e.currentTarget.value)}
          size="md"
        />
        <Button onClick={handleLink} loading={linkJira.isPending} leftSection={<IconPlugConnected size={16} />} size="md" fullWidth>
          Conectar Jira
        </Button>
        {linkJira.isError && (
          <Alert color="red" icon={<IconX size={16} />}>
            {linkJira.error instanceof Error ? linkJira.error.message : 'Falha ao vincular'}
          </Alert>
        )}
        {linkJira.isSuccess && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            Conectado com sucesso: {linkJira.data.parsed.project_key} — {linkJira.data.test_result.total_issues} issues encontradas
          </Alert>
        )}
      </Stack>
    </WidgetCard>
  )
}
